import React, { createContext, useMemo } from 'react'
import { useAuth } from './AuthContext'

export interface RBACContextType {
  hasFeature: (feature: string) => boolean
  hasAnyFeature: (features: string[]) => boolean
  allFeatures: string[]
}

export const RBACContext = createContext<RBACContextType | undefined>(undefined)

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()

  const isSuperAdmin = (role?: string) => {
    const normalizedRole = role?.trim().toLowerCase()
    return normalizedRole === 'super-admin' || normalizedRole === 'super_admin' || normalizedRole === 'superadmin'
  }

  const value = useMemo<RBACContextType>(
    () => {
      const hasFullAccess = isSuperAdmin(user?.role)
      const permissions = user?.permissions ?? []

      return {
        hasFeature: (feature: string) => hasFullAccess || permissions.includes(feature),
        hasAnyFeature: (features: string[]) => hasFullAccess || features.some((f) => permissions.includes(f)),
        allFeatures: hasFullAccess ? ['*'] : permissions,
      }
    },
    [user?.permissions, user?.role]
  )

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>
}

export const useRBAC = () => {
  const context = React.useContext(RBACContext)
  if (!context) throw new Error('useRBAC must be used within RBACProvider')
  return context
}
